import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Avatar, Box, Typography } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTranslation } from "react-i18next";
import { Navigation, Pagination, Mousewheel, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { getUser } from "@app/api";
import { Button, DataDialog, Video } from "@app/components/atoms";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import { IconInvalidUrl, IconUser } from "@app/components/icons";
import {
  IconMaintenaceNew,
  IconNotApplicableDashInspection,
} from "@app/components/icons/IconMaintenace/IconMaintenanceNew";
import { IconSelectedNew } from "@app/components/icons/IconSelected/IconSelectedNew";
import {
  INVALID_URL,
  ItemResultType,
  ResultType,
} from "@app/constants";
import { formatNumberWithCommas } from "@app/helpers";
import type {
  LatestInspectionData,
  LatestInspectionItem,
} from "./createInspectionForm.types";

type Props = {
  open: boolean;
  onClose: () => void;
  item: LatestInspectionItem | null;
  inspection: LatestInspectionData | null;
};

function resolveDisplayComment(item: LatestInspectionItem | null): string {
  if (!item) return "";

  const reportComment = item.machineReport?.reportComment;
  if (typeof reportComment === "string" && reportComment.trim()) {
    return reportComment.trim();
  }

  if (typeof item.freeText === "string" && item.freeText.trim()) {
    return item.freeText.trim();
  }

  const selectedOption = item.options?.find((option) => option.selected)?.name;
  if (typeof selectedOption === "string" && selectedOption.trim()) {
    return selectedOption.trim();
  }

  return "";
}

function isIconResultType(resultType?: string | null): boolean {
  return (
    resultType === ResultType.OK_OR_ANOMARY ||
    resultType === ResultType.OK_ANOMARY_NOT_APPLICABLE
  );
}

function isNumericLikeResult(result?: string | null): boolean {
  if (!result) return false;
  if (
    result === ItemResultType.OK ||
    result === ItemResultType.ANOMARY ||
    result === ItemResultType.NOT_APPLICABLE ||
    result === ItemResultType.NONE ||
    result === ResultType.NONE ||
    result === ResultType.FREE_TEXT ||
    result === ItemResultType.FREE_TEXT
  ) {
    return false;
  }
  return true;
}

function MetaField({ label, value }: { label: string; value: string }) {
  if (!value) return null;

  return (
    <div className="text-content">
      <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">{label}</p>
      <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
        <span dir="ltr">{value}</span>
      </p>
    </div>
  );
}

function ResultIconsRow({
  resultType,
  result,
}: {
  resultType?: string | null;
  result?: string | null;
}) {
  const { t } = useTranslation();
  const showOkAnomaly =
    resultType === ResultType.OK_OR_ANOMARY ||
    resultType === ResultType.OK_ANOMARY_NOT_APPLICABLE;
  const showNotApplicable = resultType === ResultType.OK_ANOMARY_NOT_APPLICABLE;

  if (!showOkAnomaly && !showNotApplicable) {
    if (result === ItemResultType.NONE || result === ResultType.NONE) {
      return null;
    }

    if (isNumericLikeResult(result)) {
      return (
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {formatNumberWithCommas(result ?? "")}
        </Typography>
      );
    }

    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "20px",
        flexWrap: "nowrap",
      }}
    >
      {showOkAnomaly && (
        <>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
            title={t("normal")}
          >
            <IconSelectedNew
              fillColor={result === ItemResultType.OK ? "#0e7c47" : "#DAFBE1"}
              iconColor={result === ItemResultType.OK ? "white" : "#1A7F37"}
            />
          </Box>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
            title={t("abnormal")}
          >
            <IconMaintenaceNew
              fillColor={
                result === ItemResultType.ANOMARY ? "#DA9309" : "#FFF8C5"
              }
              iconColor={
                result === ItemResultType.ANOMARY ? "white" : "#DA9309"
              }
            />
          </Box>
        </>
      )}
      {showNotApplicable && (
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
          title={t("notapplicable")}
        >
          <IconNotApplicableDashInspection
            fillColor={
              result === ItemResultType.NOT_APPLICABLE ? "#696969" : "#D3D3D3"
            }
            dashColor="white"
            size={result === ItemResultType.NOT_APPLICABLE ? 44 : 37}
          />
        </Box>
      )}
    </Box>
  );
}

export function PreviousInspectionItemDialog({
  open,
  onClose,
  item,
  inspection,
}: Props) {
  const { t } = useTranslation();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [openLocation, setOpenLocation] = useState(false);
  const [brokenMediaIndexes, setBrokenMediaIndexes] = useState<number[]>([]);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [userTimeFormat, setUserTimeFormat] =
    useState<string>("yyyy/MM/dd HH:mm");
  const [localeCode, setLocaleCode] = useState("");

  useEffect(() => {
    if (open) {
      setAvatarBroken(false);
      setBrokenMediaIndexes([]);
      setViewerOpen(false);
      setSelectedImage("");
      setOpenLocation(false);
    }
  }, [open, item?.inspectionItemId]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        const isoLocaleCode = response?.data?.data?.isoLocaleCode || "";

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setUserTimeFormat(timeFormat);
        setLocaleCode(isoLocaleCode);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const displayComment = useMemo(() => resolveDisplayComment(item), [item]);
  const medias = item?.machineReport?.machineReportMedias ?? [];
  const recordings = item?.machineReport?.machineReportRecordings ?? [];
  const reporterName = useMemo(() => {
    const givenName = inspection?.givenName?.trim() || "";
    const surname = inspection?.surname?.trim() || "";

    if (givenName || surname) {
      return localeCode === "ja"
        ? [surname, givenName].filter(Boolean).join(" ")
        : [givenName, surname].filter(Boolean).join(" ");
    }

    return item?.machineReport?.reportedBy || "";
  }, [
    inspection?.givenName,
    inspection?.surname,
    item?.machineReport?.reportedBy,
    localeCode,
  ]);
  const inspectedAt = useMemo(() => {
    if (!inspection?.inspectionAt) return "";
    try {
      return format(new Date(inspection.inspectionAt), userTimeFormat);
    } catch {
      return "";
    }
  }, [inspection?.inspectionAt, userTimeFormat]);
  const serviceMeterValue =
    inspection?.serviceMeter != null && inspection.serviceMeter !== ""
      ? formatNumberWithCommas(inspection.serviceMeter, "h")
      : "";
  const odometerValue =
    inspection?.odometer != null && inspection.odometer !== ""
      ? formatNumberWithCommas(inspection.odometer, "km")
      : "";
  const pictureUrl = inspection?.pictureUrl;
  const hasValidPicture =
    !!pictureUrl && pictureUrl !== INVALID_URL && !avatarBroken;
  const hasLocation =
    (inspection?.lat || inspection?.lat === 0) &&
    (inspection?.lng || inspection?.lng === 0);

  const hasItemContent =
    !!item &&
    (!!item.result ||
      !!item.description?.trim() ||
      !!displayComment ||
      medias.length > 0 ||
      recordings.length > 0 ||
      isIconResultType(item.resultType));

  if (!open) return null;

  return (
    <>
      <DataDialog
        title={t("createInspection.previousResult")}
        open={open}
        onClose={onClose}
        disableBackdropClick={true}
        className="dialog-report dialog-machine-report"
        dialogActions={
          <div className="flex justify-content-end">
            <Button onClick={onClose} className="btn btn-no-border">
              {t("dialogRefuel.close")}
            </Button>
          </div>
        }
      >
        {!item ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            {t("createInspection.noPreviousResult")}
          </Typography>
        ) : (
          <div className="content">
            <div className="report-card">
              <div className="content-report">
                <div className="header flex align-items-center">
                  {hasValidPicture ? (
                    <Avatar
                      sx={{ width: 40, height: 40 }}
                      alt="avatar-reporter"
                      src={pictureUrl}
                      imgProps={{
                        onError: () => setAvatarBroken(true),
                      }}
                    />
                  ) : pictureUrl === INVALID_URL || avatarBroken ? (
                    <Box
                      sx={{
                        minWidth: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <IconInvalidUrl />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <IconUser size={40} />
                    </Box>
                  )}
                  <div className="report-detail">
                    <p className="txt-gray-1 fs-12 mt-0 mb-0">
                      {t("tableMachineDetail.reporter")}
                    </p>
                    <h3 className="report-name txt-black-1 word-break txt-pre-wrap">
                      {reporterName || "-"}
                    </h3>
                  </div>
                </div>

                <MetaField
                  label={t("tableMachineDetail.date")}
                  value={inspectedAt}
                />
                {hasLocation && (
                  <div className="text-content">
                    <p className="txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap">
                      {t("tableMachineDetail.location")}
                    </p>
                    <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
                      <span
                        style={{
                          cursor: "pointer",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onClick={() => setOpenLocation(true)}
                      >
                        <span
                          style={{
                            textDecoration: "underline",
                            color: "#140a9a",
                            fontWeight: "bold",
                          }}
                        >
                          {t("openMap")}
                        </span>
                        <LaunchIcon sx={{ fontSize: 16, color: "black" }} />
                      </span>
                    </p>
                  </div>
                )}
                <MetaField
                  label={t("serviceMeterOrSMR")}
                  value={serviceMeterValue}
                />
                <MetaField label={t("odometer")} value={odometerValue} />
              </div>
            </div>

            <div className="report-card">
              <div className="content-report">
                {!hasItemContent ? (
                  <Typography sx={{ p: 2 }} color="text.secondary">
                    {t("createInspection.noPreviousResult")}
                  </Typography>
                ) : (
                  <>
                    <div className="text-content">
                      <p
                        className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap word-break"
                        style={{ fontWeight: 700 }}
                      >
                        {item.name}
                      </p>
                      {!!item.description?.trim() && (
                        <p className="txt-gray-1 fs-14 mt-4 mb-0 txt-pre-wrap word-break">
                          {item.description}
                        </p>
                      )}
                    </div>

                    {(isIconResultType(item.resultType) ||
                      (!!item.result &&
                        item.result !== ItemResultType.NONE &&
                        item.result !== ResultType.NONE)) && (
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <ResultIconsRow
                          resultType={item.resultType}
                          result={item.result}
                        />
                      </Box>
                    )}

                    {displayComment ? (
                      <div className="text-content">
                        <p className="txt-gray-1 fs-12 mt-0 mb-0">
                          {t("dialogReport.comment")}
                        </p>
                        <p className="fs-16 mt-4 mb-0 txt-black txt-pre-wrap word-break">
                          {displayComment}
                        </p>
                      </div>
                    ) : null}

                    {!!recordings.length && (
                      <div className="text-content">
                        {recordings.map((recording, index) =>
                          recording.recordingUrl ? (
                            <div
                              key={index}
                              style={{
                                marginBottom:
                                  index < recordings.length - 1 ? "12px" : 0,
                              }}
                            >
                              <audio
                                controls
                                src={recording.recordingUrl}
                                style={{ width: "100%" }}
                              />
                            </div>
                          ) : null,
                        )}
                      </div>
                    )}

                    {!!medias.length && (
                      <Swiper
                        cssMode={true}
                        navigation={true}
                        pagination={true}
                        mousewheel={true}
                        keyboard={true}
                        modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                        className="swiper mb-16"
                      >
                        {medias.map((media, mediaIndex) => {
                          const mediaUrl = media.mediaUrl;
                          const isBroken =
                            !mediaUrl ||
                            mediaUrl === INVALID_URL ||
                            brokenMediaIndexes.includes(mediaIndex);

                          return (
                            <SwiperSlide key={mediaIndex}>
                              <div className="wrapper-file">
                                {isBroken ? (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <IconInvalidUrl />
                                  </Box>
                                ) : media?.mimeType?.includes("video") ? (
                                  <Video
                                    media={media}
                                    onError={() =>
                                      setBrokenMediaIndexes((prev) =>
                                        prev.includes(mediaIndex)
                                          ? prev
                                          : [...prev, mediaIndex],
                                      )
                                    }
                                  />
                                ) : (
                                  <div
                                    className="wrapper-file"
                                    onClick={() => {
                                      if (mediaUrl) {
                                        setSelectedImage(mediaUrl);
                                        setViewerOpen(true);
                                      }
                                    }}
                                    style={{ cursor: "zoom-in" }}
                                  >
                                    <Box
                                      className="swiper-image img-report"
                                      component="img"
                                      alt="swiper-image"
                                      src={mediaUrl}
                                      onError={() =>
                                        setBrokenMediaIndexes((prev) =>
                                          prev.includes(mediaIndex)
                                            ? prev
                                            : [...prev, mediaIndex],
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DataDialog>

      <DataDialog
        title={t("tableMachineDetail.location")}
        open={openLocation}
        setOpen={setOpenLocation}
        className="dialog-location-report"
        disableBackdropClick={true}
      >
        {hasLocation && inspection && (
          <>
            <iframe
              title="map-location"
              className="map-iframe"
              loading="lazy"
              src={`https://maps.google.com/maps?&q="+${inspection.lat}, ${inspection.lng}"&output=embed`}
            />
            <span className="fs-14 txt-gray-1 label-dialog">
              {t("tableMachineDetail.date")}
            </span>
            <p className="fs-16 txt-gray mt-4">
              {inspectedAt || "-"}
              <br />
            </p>
            <span className="fs-14 txt-gray-1 label-dialog">
              {t("tableMachineDetail.reporter")}
            </span>
            <div className="flex-centered justify-content-start mt-8">
              {hasValidPicture ? (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                  }}
                  alt="avatar-reporter"
                  src={pictureUrl}
                  imgProps={{
                    onError: () => setAvatarBroken(true),
                  }}
                />
              ) : pictureUrl === INVALID_URL || avatarBroken ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconInvalidUrl />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconUser />
                </Box>
              )}

              <span className="fs-16 txt-gray ml-5 word-break txt-pre-wrap">
                {reporterName || "-"}
              </span>
            </div>
            <div className="flex justify-content-end">
              <Button
                onClick={() => setOpenLocation(false)}
                className="btn btn-no-border"
              >
                {t("dialogRefuel.close")}
              </Button>
            </div>
          </>
        )}
      </DataDialog>

      {viewerOpen && selectedImage && (
        <CustomImageViewer
          imageUrl={selectedImage}
          onClose={() => {
            setViewerOpen(false);
            setSelectedImage("");
          }}
        />
      )}
    </>
  );
}
